import { useEffect, useRef, useState } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useSelector } from "react-redux";
import useApi from "../../../helpers/apiHelper/requestHelper";
import "./CommunityAreaChart.css";

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const year = date.getUTCFullYear();
  return `${day}-${month}-${year}`;
};

export const TABS = [
  { label: "Daily", duration: "Day" },
  { label: "Monthly", duration: "Month" },
  { label: "Weekly", duration: "Week" },
  { label: "All Time", duration: "All Time" },
];

// Standalone tabs component — use in a parent when you want tabs outside the chart
export const CommunityAreaChartTabs = ({ activeTab, onTabChange }) => (
  <div className="cac-tabs">
    {TABS.map((tab, i) => (
      <button
        key={tab.duration}
        className={`cac-tab${activeTab === i ? " cac-tab--active" : ""}`}
        onClick={() => onTabChange(i)}
      >
        {tab.label}
      </button>
    ))}
  </div>
);

const CustomTooltip = ({ active, payload, label, graphType }) => {
  if (!active || !payload || !payload.length) return null;
  const value = payload[0]?.value ?? 0;
  const dateRange = payload[0]?.payload?.dateRange;
  const dateLabel = payload[0]?.payload?.dateLabel;
  const formatted =
    graphType === "subscribers"
      ? value.toLocaleString()
      : graphType === "growth"
      ? `${value.toLocaleString()}%`
      : `$${value.toLocaleString()}`;
  const displayLabel = dateRange || dateLabel || label;

  return (
    <div className="cac-tooltip">
      <p className="cac-tooltip-value">{formatted}</p>
      {displayLabel && <p className="cac-tooltip-label">{displayLabel}</p>}
    </div>
  );
};

// activeTab + onTabChange — optional. If provided, tabs are controlled by parent (internal tabs hidden).
// If omitted, component is self-contained with its own tabs.
const CommunityAreaChart = ({
  graphType = "pnl",
  userId,
  activeTab: activeTabProp,
  onTabChange,
  bare = false,
  market,
}) => {
  const isControlled = activeTabProp !== undefined;
  const [internalTab, setInternalTab] = useState(0);

  const activeTab = isControlled ? activeTabProp : internalTab;
  const setActiveTab = isControlled ? onTabChange : setInternalTab;

  const [chartData, setChartData] = useState([]);
  const [chartHeight, setChartHeight] = useState(bare ? 0 : 250);
  const chartContainerRef = useRef(null);
  const makeApiCall = useApi();
  const user = useSelector((state) => state?.user?.user);
  const duration = TABS[activeTab]?.duration ?? TABS[0].duration;

  useEffect(() => {
    if (!bare || !chartContainerRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      if (entry.contentRect.height > 0) setChartHeight(entry.contentRect.height);
    });
    ro.observe(chartContainerRef.current);
    return () => ro.disconnect();
  }, [bare]);

  useEffect(() => {
    if (!userId) return;
    const fetchData = async () => {
      const produration = duration.toLowerCase();
      try {
        const json = await makeApiCall(
          "get",
          `historyGraph?duration=${produration}&graphType=${graphType}&userId=${userId}${market ? `&market=${market}` : ""}`,
          { duration: produration, graphType, userId, ...(market ? { market } : {}) }
        );

        if (json?.status && Array.isArray(json.data)) {
          const responseData = json.data;

          // Monthly: always show all 12 months of current year
          if (produration === "month") {
            const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
            const currentYear = new Date().getFullYear();
            const dataMap = {};
            responseData.forEach((item) => {
              const match = item.key && item.key.match(/^([A-Za-z]+)\s+(\d{4})/);
              if (match) {
                const idx = new Date(`${match[1]} 1, ${match[2]}`).getMonth();
                if (parseInt(match[2]) === currentYear) dataMap[idx] = item.value;
              }
            });
            setChartData(monthNames.map((name, i) => ({ label: name, value: dataMap[i] ?? 0 })));
            return;
          }

          const hasData = responseData.length > 0 && responseData.some(
            (item) => item.value !== 0 && item.value !== null && item.value !== undefined
          );

          if (!hasData) {
            setChartData([{ label: "0", value: 0 }]);
            return;
          }

          const actualData = responseData.filter(
            (item) =>
              item.key !== "" && item.key !== null && item.key !== undefined &&
              item.value !== null && item.value !== undefined
          );

          // Daily: all days of current month
          if (produration === "day") {
            const dataMap = {};
            actualData.forEach((item) => {
              const match = item.key && item.key.match(/(\d{4}-\d{2}-\d{2})/);
              if (match) dataMap[match[1]] = item.value;
            });
            const today = new Date();
            const year = today.getUTCFullYear();
            const month = today.getUTCMonth();
            const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
            const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
            const allDays = [];
            for (let day = 1; day <= daysInMonth; day++) {
              const key = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
              allDays.push({
                label: String(day),
                value: dataMap[key] ?? 0,
                dateLabel: `${String(day).padStart(2, "0")}/${String(month + 1).padStart(2, "0")}`,
              });
            }
            setChartData(allDays);
            return;
          }

          // Weekly: weeks from user's join week up to current week
          if (produration === "week") {
            const dataMap = {};
            actualData.forEach((item) => {
              const match = item.key && item.key.match(/Week (\d+)/);
              if (match) dataMap[parseInt(match[1])] = item.value;
            });

            const getISOWeek = (date) => {
              const d = new Date(date);
              d.setHours(0, 0, 0, 0);
              d.setDate(d.getDate() + 4 - (d.getDay() || 7));
              const yearStart = new Date(d.getFullYear(), 0, 1);
              return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
            };

            const now = new Date();
            const currentYear = now.getFullYear();
            const currentWeek = getISOWeek(now);

            const joinDate = user?.createdAt ? new Date(user.createdAt) : now;
            const joinYear = joinDate.getFullYear();
            const startWeek = joinYear === currentYear ? getISOWeek(joinDate) : 1;

            const jan4 = new Date(currentYear, 0, 4);
            const jan4Day = jan4.getDay() || 7;
            const week1Monday = new Date(jan4);
            week1Monday.setDate(jan4.getDate() - (jan4Day - 1));

            const fmt = (dt) =>
              `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}`;

            const allWeeks = [];
            for (let w = startWeek; w <= currentWeek; w++) {
              const monday = new Date(week1Monday);
              monday.setDate(week1Monday.getDate() + (w - 1) * 7);
              const sunday = new Date(monday);
              sunday.setDate(monday.getDate() + 6);
              allWeeks.push({
                label: String(w),
                value: dataMap[w] ?? 0,
                dateRange: `${fmt(monday)} - ${fmt(sunday)}`,
              });
            }
            setChartData(allWeeks);
            return;
          }

          // All Time: last 14 entries
          const last14 = actualData.slice(-14);
          setChartData(
            last14.map((d) => {
              const match = d.key.match(/(\d{4})-(\d{2})-(\d{2})/);
              const label = match ? `${match[3]}-${match[2]}` : d.key;
              return { label, value: d.value };
            })
          );
        } else {
          setChartData([{ label: formatDate(user?.createdAt), value: 0 }]);
        }
      } catch (e) {
        console.error("CommunityAreaChart fetch error:", e);
        setChartData([{ label: "0", value: 0 }]);
      }
    };

    fetchData();
  }, [duration, graphType, userId, market]);

  return (
    <div className={bare ? "cac-wrapper cac-wrapper--bare" : "cac-wrapper"}>
      {/* Internal tabs — only rendered when parent is not controlling */}
      {!isControlled && (
        <CommunityAreaChartTabs activeTab={activeTab} onTabChange={setActiveTab} />
      )}

      <div className="cac-chart" ref={chartContainerRef}>
        <ResponsiveContainer width="100%" height={bare ? chartHeight || 250 : 250}>
          <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="cacGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#11b700" stopOpacity={0.25} />
                <stop offset="95%" stopColor="#11b700" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(46, 107, 199, 0.15)" vertical={false} />
            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#9ca3af", fontSize: 12 }}
              interval={Math.max(0, Math.floor((chartData.length - 1) / 6))}
            />
            <YAxis hide />
            <Tooltip
              content={<CustomTooltip graphType={graphType} />}
              cursor={{ stroke: "#11b700", strokeWidth: 1 }}
            />
            <Area
              type="linear"
              dataKey="value"
              stroke="#11b700"
              strokeWidth={2}
              fill="url(#cacGradient)"
              dot={false}
              activeDot={{ r: 5, fill: "#ffffff", stroke: "#11b700", strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default CommunityAreaChart;
